import * as CC from 'vanilla-cookieconsent'
import {disableGA, loadGA, updateConsent} from '@/app-core/lib/services/GoogleAnalytics.js'
import {applyOneSignalConsent} from '@/app-core/lib/services/OneSignal.js'
import DataStore from '@/core/lib/data/dataStore/DataStore.js'

// Το cookie domain του GA (π.χ. ".example.com") για σωστό σβήσιμο
const cookieDomain = '.' + window.location.hostname.replace(/^www\./, '')
const gfpId = DataStore?.App?.Config?.Analytics?.Google?.GfpId || ''
const clientId = DataStore?.App?.Config?.Analytics?.Google?.ClientId || ''
const GA_IDS = [...new Set([clientId, gfpId].filter(Boolean))]
const GA_CONFIG = {
  send_page_view: true,
  anonymize_ip  : true
}

export default {
  mixins: [],
  props : [],
  data() {
    return {
      consent: {analytics: false, marketing: false, notifications: false},

      ccConfig: {
        disablePageInteraction: false, // dark overlay, the page is not clickable until the user selects
        revision              : 1, // if you increase it, it asks for consent from everyone again

        categories: {
          necessary: {},

          analytics: {
            // When the user rejects, GA cookies are deleted automatically
            autoClear: {
              cookies: [
                {name: /^_ga/, domain: cookieDomain},
                {name: '_gid', domain: cookieDomain}
              ],
              // GA can't be "unloaded" — reload the page for a clean state
              reloadPage: true
            },
            services: {
              ga4: {
                label  : 'Google Analytics 4',
                cookies: [{name: /^_ga/}]
              }
            }
          },
          marketing: {
            services: {
              googleAds: {label: 'Google Ads / Remarketing'}
            }
          },
          notifications: {
            services: {
              onesignal: {label: 'OneSignal (Push Notifications)'}
            }
          }
        },

        language: {
          translations: {
            en: {
              consentModal: {
                title             : 'We use cookies',
                description       : 'We use cookies and other similar technologies to provide you with the best possible service and to analyze the use of our platform. You can change your settings at any time.',
                acceptAllBtn      : 'Accept All',
                acceptNecessaryBtn: 'Accept Required',
                showPreferencesBtn: 'Settings',
                footer            : `<a href="/${this.$i18n.locale}/page/policies/privacy">${this.$t('Page.PoliciesPrivacy.Name')}</a> <a href="/${this.$i18n.locale}/page/policies/cookies">${this.$t('Page.PoliciesCookies.Name')}</a>`
              },
              preferencesModal: {
                title              : 'Cookie settings',
                acceptAllBtn       : 'Accept All',
                acceptNecessaryBtn : 'Accept Required',
                savePreferencesBtn : 'Save Options',
                closeIconLabel     : 'Close',
                serviceCounterLabel: 'Service|Services',
                sections           : [
                  {
                    title      : 'Use of cookies',
                    description: 'You can choose which categories of cookies you allow. You can change your choices at any time.'
                  },
                  {
                    title         : 'Necessary <span class="pm__badge">Always enabled</span>',
                    description   : 'Necessary for the basic functioning of the website.',
                    linkedCategory: 'necessary',
                    cookieTable   : {
                      headers: {name: 'Name', domain: 'Domain', desc: 'Description', duration: 'Duration'},
                      body   : [
                        {
                          name    : 'x-session',
                          domain  : location.hostname,
                          desc    : 'localStorage — User session id',
                          duration: 'Forever'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_KkeejKKrYUI3DKE14qTczw==',
                          domain  : location.hostname,
                          desc    : 'localStorage — User authentication token',
                          duration: 'Until Logout'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_OxjeauhOaBHFfJG5qXtA0w==',
                          domain  : location.hostname,
                          desc    : 'localStorage — User details',
                          duration: 'Until Logout'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_dark',
                          domain  : location.hostname,
                          desc    : 'localStorage — Theme preference',
                          duration: 'Forever'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_mU3WnA73cNNol1mDg+eyrw==',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Last page visited',
                          duration: 'Until user session ends'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_fsJJ7FW2oSJqqoUcErgmbQ==',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Order identifier',
                          duration: 'Until order is completed'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_t',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Application type',
                          duration: 'Until user session ends'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_d',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Scanned QR type',
                          duration: 'Until user session ends'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_QRData',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Scanned QR data',
                          duration: 'Until user session ends'
                        }
                      ]
                    }
                  },
                  {
                    title         : 'Statistics',
                    description   : 'They help us understand how the website is used.',
                    linkedCategory: 'analytics',
                    cookieTable   : {
                      headers: {name: 'Name', domain: 'Domain', desc: 'Description', duration: 'Duration'},
                      body   : [
                        {
                          name    : '_ga',
                          domain  : location.hostname,
                          desc    : 'Google Analytics 4 — distinguishes unique visitors',
                          duration: '2 years'
                        },
                        {
                          // One cookie per Measurement ID (e.g. _ga_AAAAAAAAAA, _ga_BBBBBBBBBB)
                          name    : '_ga_&lt;ID&gt;',
                          domain  : location.hostname,
                          desc    : 'Google Analytics 4 — maintains session state',
                          duration: '2 years'
                        }
                      ]
                    }
                  },
                  {
                    title         : 'Notifications',
                    description   : 'They allow push notifications to be sent via OneSignal. They store a subscription identifier in the browser.',
                    linkedCategory: 'notifications',
                    cookieTable   : {
                      headers: {name: 'Name', domain: 'Domain', desc: 'Description', duration: 'Duration'},
                      body   : [
                        {
                          name    : '__cf_bm',
                          domain  : 'onesignal.com',
                          // Third-party cookie set by Cloudflare on OneSignal's domain.
                          // It can't be deleted from our site (different domain), so it is
                          // only disclosed here, not listed in autoClear.
                          desc    : 'Third-party cookie (Cloudflare) — bot protection for OneSignal service',
                          duration: '30 minutes'
                        },
                        {
                          name    : 'ONE_SIGNAL_SDK_DB',
                          domain  : location.hostname,
                          desc    : 'IndexedDB — push subscription ID and SDK settings',
                          duration: 'Until consent is withdrawn'
                        },
                        {
                          name    : 'onesignal*',
                          domain  : location.hostname,
                          desc    : 'localStorage — SDK status and prompt display',
                          duration: 'Until consent is withdrawn'
                        },
                        {
                          name    : 'OneSignalSDKWorker.js',
                          domain  : location.hostname,
                          desc    : 'Service worker — receive and display push notifications',
                          duration: 'Until deregistration'
                        }
                      ]
                    }
                  },
                  {
                    title      : 'More information',
                    description: `For questions, contact us from the <a href="/${this.$i18n.locale}/page/contact">contact</a> page.`
                  }
                ]
              }
            },
            el: {
              consentModal: {
                title             : 'Χρησιμοποιούμε cookies',
                description       : 'Χρησιμοποιούμε cookies και άλλες παρόμοιες τεχνολογίες για να σου παρέχουμε την καλύτερη δυνατή εξυπηρέτηση και να αναλύουμε τη χρήση της πλατφόρμας μας. Μπορείς να αλλάξεις τις ρυθμίσεις σου ανά πάσα στιγμή.',
                acceptAllBtn      : 'Αποδοχή Όλων',
                acceptNecessaryBtn: 'Αποδοχή Απαραίτητων',
                showPreferencesBtn: 'Ρυθμίσεις',
                footer            : `<a href="/${this.$i18n.locale}/page/policies/privacy">${this.$t('Page.PoliciesPrivacy.Name')}</a> <a href="/${this.$i18n.locale}/page/policies/cookies">${this.$t('Page.PoliciesCookies.Name')}</a>`
              },
              preferencesModal: {
                title              : 'Ρυθμίσεις cookies',
                acceptAllBtn       : 'Αποδοχή Όλων',
                acceptNecessaryBtn : 'Αποδοχή Απαραίτητων',
                savePreferencesBtn : 'Αποθήκευση Επιλογών',
                closeIconLabel     : 'Κλείσιμο',
                serviceCounterLabel: 'Υπηρεσία|Υπηρεσίες',
                sections           : [
                  {
                    title      : 'Χρήση cookies',
                    description: 'Μπορείτε να επιλέξετε ποιες κατηγορίες cookies επιτρέπετε. Μπορείτε να αλλάξετε τις επιλογές σας οποιαδήποτε στιγμή.'
                  },
                  {
                    title         : 'Απαραίτητα <span class="pm__badge">Πάντα ενεργά</span>',
                    description   : 'Απαιτούνται για τη βασική λειτουργία του ιστότοπου.',
                    linkedCategory: 'necessary',
                    cookieTable   : {
                      headers: {name: 'Όνομα', domain: 'Domain', desc: 'Περιγραφή', duration: 'Διάρκεια'},
                      body   : [
                        {
                          name    : 'x-session',
                          domain  : location.hostname,
                          desc    : 'localStorage — Αναγνωριστικό συνεδρίας χρήστη',
                          duration: 'Πάντα'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_KkeejKKrYUI3DKE14qTczw==',
                          domain  : location.hostname,
                          desc    : 'localStorage — Διακριτικό ελέγχου ταυτότητας χρήστη',
                          duration: 'Μέχρι την αποσύνδεση'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_OxjeauhOaBHFfJG5qXtA0w==',
                          domain  : location.hostname,
                          desc    : 'localStorage — Στοιχεία χρήστη',
                          duration: 'Μέχρι την αποσύνδεση'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_dark',
                          domain  : location.hostname,
                          desc    : 'localStorage — Προτίμηση θέματος',
                          duration: 'Πάντα'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_mU3WnA73cNNol1mDg+eyrw==',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Τελευταία σελίδα που επισκεφθήκατε',
                          duration: 'Μέχρι να τελειώσει η συνεδρία χρήστη'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_fsJJ7FW2oSJqqoUcErgmbQ==',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Αναγνωριστικό παραγγελίας',
                          duration: 'Μέχρι την ολοκλήρωση της παραγγελίας'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_t',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Τύπος εφαρμογής',
                          duration: 'Μέχρι να τελειώσει η συνεδρία χρήστη'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_d',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Τύπος σαρωμένου QR',
                          duration: 'Μέχρι να τελειώσει η συνεδρία χρήστη'
                        },
                        {
                          name    : 'mp-&lt;NAME&gt;_QRData',
                          domain  : location.hostname,
                          desc    : 'sessionStorage — Δεδομένα σαρωμένου QR',
                          duration: 'Μέχρι να τελειώσει η συνεδρία χρήστη'
                        }
                      ]
                    }
                  },
                  {
                    title         : 'Στατιστικά',
                    description   : 'Μας βοηθούν να καταλάβουμε πώς χρησιμοποιείται ο ιστότοπος.',
                    linkedCategory: 'analytics',
                    cookieTable   : {
                      headers: {name: 'Όνομα', domain: 'Domain', desc: 'Περιγραφή', duration: 'Διάρκεια'},
                      body   : [
                        {
                          name    : '_ga',
                          domain  : location.hostname,
                          desc    : 'Google Analytics 4 — διακρίνει μοναδικούς επισκέπτες',
                          duration: '2 χρόνια'
                        },
                        {
                          // One cookie per Measurement ID (e.g. _ga_AAAAAAAAAA, _ga_BBBBBBBBBB)
                          name    : '_ga_&lt;ID&gt;',
                          domain  : location.hostname,
                          desc    : 'Google Analytics 4 — διατηρεί την κατάσταση της συνεδρίας',
                          duration: '2 χρόνια'
                        }
                      ]
                    }
                  },
                  {
                    title         : 'Ειδοποιήσεις',
                    description   : 'Επιτρέπουν την αποστολή push ειδοποιήσεων μέσω της OneSignal. Αποθηκεύουν στον browser ένα αναγνωριστικό συνδρομής.',
                    linkedCategory: 'notifications',
                    cookieTable   : {
                      headers: {name: 'Όνομα', domain: 'Domain', desc: 'Περιγραφή', duration: 'Διάρκεια'},
                      body   : [
                        {
                          name    : '__cf_bm',
                          domain  : 'onesignal.com',
                          // Third-party cookie set by Cloudflare on OneSignal's domain.
                          // It can't be deleted from our site (different domain), so it is
                          // only disclosed here, not listed in autoClear.
                          desc    : 'Cookie τρίτου (Cloudflare) — προστασία από bots για την υπηρεσία OneSignal',
                          duration: '30 λεπτά'
                        },
                        {
                          name    : 'ONE_SIGNAL_SDK_DB',
                          domain  : location.hostname,
                          desc    : 'IndexedDB — αναγνωριστικό συνδρομής push και ρυθμίσεις SDK',
                          duration: 'Μέχρι την ανάκληση της συγκατάθεσης'
                        },
                        {
                          name    : 'onesignal*',
                          domain  : location.hostname,
                          desc    : 'localStorage — κατάσταση SDK και εμφάνισης prompt',
                          duration: 'Μέχρι την ανάκληση της συγκατάθεσης'
                        },
                        {
                          name    : 'OneSignalSDKWorker.js',
                          domain  : location.hostname,
                          desc    : 'Service worker — λήψη και εμφάνιση push ειδοποιήσεων',
                          duration: 'Μέχρι την απεγγραφή'
                        }
                      ]
                    }
                  },
                  {
                    title      : 'Περισσότερες πληροφορίες',
                    description: `Για απορίες επικοινωνήστε μαζί μας από τη σελίδα <a href="/${this.$i18n.locale}/page/contact">επικοινωνίας</a>.`
                  }
                ]
              }
            }
          }
        }
      }
    }
  },
  computed: {},
  watch   : {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    // Runs on every page load when there is already valid consent,
    // and immediately after the user's first selection.
    applyCookieConsent() {
      this.consent.analytics = CC.acceptedCategory('analytics')
      this.consent.marketing = CC.acceptedCategory('marketing')
      this.consent.notifications = CC.acceptedCategory('notifications')

      updateConsent(this.consent)

      if (this.consent.analytics) {
        loadGA(CC.loadScript, GA_IDS, GA_CONFIG)
      }

      applyOneSignalConsent(this.consent.notifications, DataStore.App.Config.Notifications)
    },

    // The user changed their options from the preferences modal
    onCookieConsentChange({changedCategories}) {
      if (['analytics', 'marketing', 'notifications'].some((c) => changedCategories.includes(c))) {
        this.applyCookieConsent()
      }
      // If analytics is rejected, autoClear + reloadPage takes care of the rest.
      // Additionally, we explicitly disable GA for the current page:
      if (!this.consent.analytics) {
        disableGA(GA_IDS)
      }
    }
  }
}
